import { RefCallback, useCallback, useEffect, useRef } from 'react';
import { FOCUS_SELECTOR, focusable, tabbable } from '../utils/tabbable';
import { scopeTab } from '../utils/scope-tab';

/**
 * Hook to trap focus within a specific HTML element.
 *
 * @param active - Whether the focus trap is active or not.
 * @param onClose - Callback function to call when the focus trap is closed (e.g., on Escape key press).
 * @return A ref callback that can be attached to an HTML element to trap focus within it.
 */
export function useFocusTrap(active = true, onClose: () => void): RefCallback<HTMLElement | null> {
  const ref = useRef<HTMLElement>(null);

  // focusNode function to focus the first focusable element within the node
  const focusNode = (node: HTMLElement) => {
    let focusElement: HTMLElement | null = node.querySelector('[data-autofocus]');

    if (!focusElement) {
      const children = Array.from<HTMLElement>(node.querySelectorAll(FOCUS_SELECTOR));
      focusElement = children.find(tabbable) || children.find(focusable) || null;
      if (!focusElement && focusable(node)) {
        focusElement = node;
      }
    }

    if (focusElement) {
      focusElement.focus({ preventScroll: true });
    }
  };

  // setRef function to assign the ref and focus the node
  const setRef = useCallback(
    (node: HTMLElement | null) => {
      if (!active) {
        return;
      }

      if (node === null) {
        return;
      }

      if (ref.current === node) {
        return;
      }

      if (node) {
        // Delay processing the HTML node by a frame. This ensures focus is assigned correctly.
        setTimeout(() => {
          if (node.getRootNode()) {
            focusNode(node);
          }
        });

        ref.current = node;
      } else {
        ref.current = null;
      }
    },
    [active],
  );

  // Effect to handle focus trapping when the component is active
  useEffect(() => {
    if (!active) {
      return undefined;
    }

    ref.current && setTimeout(() => focusNode(ref.current!));

    // Handle keydown events to trap focus and close on Escape key
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab' && ref.current) {
        scopeTab(ref.current, event);
      }
      // call onClose on Escape key press
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.stopImmediatePropagation();
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active]);

  return setRef;
}
